import { LOAD_NUMBER } from 'config'
import { yayApi } from '../baseApi'
import { getDeviceId } from 'lib/user'
import {
  Post,
  Group,
  User,
  YayApiResponse,
  UrlMetaData,
  PostCreated,
  ConferenceCall,
  PostCreate,
} from 'lib/_types'
import {
  pathApiGroupPostsSearch,
  pathApiPostsActiveCall,
  pathApiPostsLike,
  pathApiPostsLikers,
  pathApiPostsNew,
  pathApiPostsNewConferenceCall,
  pathApiPostsNewSharePost,
  pathApiPostsRepost,
  pathApiPostsReposts,
  pathApiPostsSearch,
  pathApiPostsShow,
  pathApiPostsShowV2,
  pathApiPostsUnlike,
  pathApiPostsUrlMetadata,
  pathApiPostsValidate,
  pathApiRecommendedRespond,
  pathApiSurveysVote,
} from 'lib/paths'
import { axiosBaseQuery } from 'store/libs/baseQuery'

export const postApi = yayApi
  .enhanceEndpoints({
    addTagTypes: [
      'Post',
      'PostLiker',
      'PostReposts',
      'Posts',
      'Posts/Hashtag',
      'Calls/All',
      'Calls/Following',
      'Calls/Group',
      'Hidden/Words',
      'UrlMetaData',
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getPost: build.query<
        {
          result: 'success'
          group: Group
          post: Post
        },
        {
          postId: string
        }
      >({
        providesTags: ['Post'],
        query: ({ postId }) => ({
          url: pathApiPostsShowV2({ postId }),
          params: {},
        }),
      }),

      getPostMutate: build.mutation<
        {
          result: 'success'
          post: Post
          group: Group
        },
        { postId: string }
      >({
        query: ({ postId }) => ({
          url: pathApiPostsShowV2({ postId }),
        }),
      }),

      getPostLikers: build.query<
        {
          users: Array<User>
          result: 'success'
          last_id: number
        },
        {
          postId: string
        }
      >({
        providesTags: ['PostLiker'],
        query: ({ postId }) => ({
          url: pathApiPostsLikers({ postId }),
          params: {},
        }),
      }),

      getPostReposts: build.query<
        {
          posts: Array<Post>
        },
        {
          postId: string
          fromId: string
        }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'PostReposts', id: args?.fromId }]
        },
        query: ({ postId, fromId }) => ({
          url: pathApiPostsReposts({ postId }),
          params: {
            number: LOAD_NUMBER,
            ...(fromId && fromId != '0' && { from_post_id: fromId }),
          },
        }),
        keepUnusedDataFor: 0,
      }),

      getMyPosts: build.query({
        providesTags: ['Posts'],
        query: ({ fromId, includeGroupPost }) => ({
          url: '/v2/posts/mine',
          params: {
            number: 15,
            from_post_id: fromId,
            include_group_post: includeGroupPost,
          },
        }),
      }),

      getUserPosts: build.query({
        providesTags: ['Posts'],
        query: ({ id, fromId }) => ({
          url: '/v2/posts/user_timeline',
          params: {
            number: 15,
            user_id: id,
            from_post_id: fromId,
          },
        }),
      }),

      getUserAlbumList: build.query({
        providesTags: ['Posts'],
        query: ({ id, fromId }) => ({
          url: '/v2/posts/user_timeline',
          params: {
            user_id: id,
            from_post_id: fromId,
            post_type: 'media',
            number: 10,
          },
        }),
        transformResponse(baseQueryReturnValue) {
          return baseQueryReturnValue?.posts || []
        },
      }),

      getGroupAlbumList: build.query({
        providesTags: ['Posts'],
        query: ({ id, fromId }) => ({
          url: '/v2/posts/group_timeline',
          params: {
            group_id: id,
            from_post_id: fromId,
            post_type: 'media',
            number: 10,
          },
        }),
        transformResponse(baseQueryReturnValue) {
          return baseQueryReturnValue?.posts || []
        },
      }),

      deleteSelectedPosts: build.mutation({
        invalidatesTags: ['Posts'],
        query: (postsIds) => ({
          url: '/v2/posts/mass_destroy',
          method: 'POST',
          data: {
            posts_ids: postsIds,
          },
        }),
        transformResponse: (response, meta, args) => {
          return {
            response,
            meta,
            args,
          }
        },
      }),

      deleteAllPosts: build.mutation<any, void>({
        invalidatesTags: ['Posts'],
        query: () => ({
          url: '/v1/posts/delete_all_post',
          method: 'POST',
        }),
      }),

      getCallTimeline: build.query({
        providesTags: ['Calls/All'],
        query: ({
          fromTimestamp,
          number = LOAD_NUMBER,
          categoryId,
          includeGroupCall = false,
          excludeGomimushi = true,
          similarAge = true,
          includeVideoCall = true,
          sharedInterests = false,
        }) => ({
          url: '/v2/posts/call_timeline',
          params: {
            number,
            from_timestamp: fromTimestamp,
            include_circle_call: includeGroupCall,
            cross_generation: !similarAge,
            exclude_recent_gominushi: excludeGomimushi,
            shared_interest_categories: sharedInterests,
            ...(categoryId && { category_id: categoryId }),
            ...(!includeVideoCall && { call_type: 'voice' }),
          },
        }),
      }),

      getFollowersCallTimeline: build.query({
        providesTags: ['Calls/Following'],
        query: ({
          fromTimestamp,
          number = LOAD_NUMBER,
          categoryId,
          includeGroupCall = false,
          excludeGomimushi = true,
          similarAge = true,
          includeVideoCall = true,
        }) => ({
          url: '/v2/posts/call_followers_timeline',
          params: {
            number,
            from_timestamp: fromTimestamp,
            include_circle_call: includeGroupCall,
            cross_generation: !similarAge,
            exclude_recent_gominushi: excludeGomimushi,
            ...(categoryId && { category_id: categoryId }),
            ...(!includeVideoCall && { call_type: 'voice' }),
          },
        }),

        transformResponse: (data) => {
          const posts = data?.posts || []
          const filteredPosts = posts.filter(
            (post: any) => post?.conference_call?.active
          )

          return {
            ...data,
            posts: filteredPosts,
          }
        },
      }),

      getGroupCallTimeline: build.query({
        providesTags: ['Calls/Group'],
        query: ({
          fromTimestamp,
          number = LOAD_NUMBER,
          groupId,
          categoryId,
        }) => ({
          url: '/v2/posts/call_timeline',
          params: {
            number,
            group_id: groupId,
            from_timestamp: fromTimestamp,
            ...(categoryId && { category_id: categoryId }),
          },
        }),
      }),

      getHiddenWords: build.query({
        providesTags: ['Hidden/Words'],
        query: () => ({
          url: '/v1/hidden/words',
        }),
      }),

      createHiddenWord: build.mutation({
        invalidatesTags: ['Hidden/Words'],
        query: ({ keyword, context }) => ({
          url: '/v1/hidden/words',
          method: 'POST',
          data: {
            word: keyword,
            context,
          },
        }),
      }),

      updateHiddenWord: build.mutation({
        invalidatesTags: ['Hidden/Words'],
        query: ({ id, context }) => ({
          url: `/v1/hidden/words/${id}`,
          method: 'PUT',
          data: {
            context,
          },
        }),
      }),

      deleteHiddenWord: build.mutation({
        invalidatesTags: ['Hidden/Words'],
        query: ({ id }) => ({
          url: `/v1/hidden/words`,
          method: 'DELETE',
          data: {
            ids: [id],
          },
        }),
      }),

      like: build.mutation({
        query: ({ postId }) => ({
          url: pathApiPostsLike,
          method: 'POST',
          data: {
            post_ids: [postId],
          },
        }),
      }),

      unlike: build.mutation<
        any,
        {
          postId: number
        }
      >({
        query: ({ postId }) => ({
          url: pathApiPostsUnlike({ postId }),
          method: 'POST',
        }),
        transformResponse: (response, meta, args) => {
          return {
            response,
            meta,
            args,
          }
        },
      }),

      respondRecommendedPost: build.mutation({
        query: ({ userId, postId, en, vn, feedbackType }) => ({
          url: pathApiRecommendedRespond({ postId }),
          method: 'POST',
          data: {
            user_id: userId,
            en,
            vn,
            feedback_result: feedbackType,
          },
        }),
      }),

      createPost: build.mutation<PostCreated, PostCreate>({
        queryFn: async (data, api) => {
          data.uuid = getDeviceId()

          return await axiosBaseQuery({ client: 'self' })(
            {
              url: pathApiPostsNew,
              method: 'POST',
              data,
            },
            api
          )
        },
      }),

      createRepost: build.mutation<PostCreated, PostCreate>({
        query: (data) => ({
          url: pathApiPostsRepost,
          method: 'POST',
          data,
        }),
        transformResponse: ({ post }: { post: PostCreated }) => post,
      }),

      createConferenceCall: build.mutation<
        {
          result: string
          conference_call: ConferenceCall
        },
        PostCreate
      >({
        query: (data) => ({
          url: pathApiPostsNewConferenceCall,
          method: 'POST',
          data,
        }),
      }),

      editPost: build.mutation<
        PostCreated,
        Pick<
          PostCreate,
          'post_id' | 'text' | 'font_size' | 'color' | 'message_tags'
        >
      >({
        query: ({ post_id, text, font_size, color, message_tags }) => ({
          url: pathApiPostsShow({ postId: post_id }),
          method: 'PUT',
          data: {
            text,
            font_size,
            color,
            message_tags,
          },
        }),
      }),

      createSurveysVote: build.mutation<
        any,
        {
          postId: number
          surveyId: number
          choiceId: number
        }
      >({
        query: ({ postId, surveyId, choiceId }) => ({
          url: pathApiSurveysVote({ surveyId }),
          method: 'POST',
          data: {
            post_id: postId,
            survey_id: surveyId,
            choice_id: choiceId,
          },
        }),
      }),

      createNewSharePost: build.mutation<
        YayApiResponse<{ post: PostCreated }>,
        {
          shareableType: 'group' | 'thread'
          shareableId: number
          text: string
          groupId?: number
        }
      >({
        query: ({ shareableType, shareableId, text, groupId }) => ({
          url: pathApiPostsNewSharePost,
          method: 'POST',
          data: {
            shareable_type: shareableType,
            shareable_id: shareableId,
            text,
            ...(groupId && { group_id: groupId }),
          },
        }),
      }),

      searchHashtag: build.query<
        YayApiResponse<{
          tags: { tag: string; post_hashtags_count: number }[]
        }>,
        { keyword: string }
      >({
        providesTags: ['Posts/Hashtag'],
        query: ({ keyword }) => ({
          url: '/v1/posts/recommended_tag',
          method: 'POST',
          data: { tag: keyword },
        }),
      }),

      getUrlMetaData: build.mutation<
        UrlMetaData,
        {
          url: string
        }
      >({
        query: ({ url }) => ({
          url: pathApiPostsUrlMetadata,
          params: {
            url,
          },
        }),
      }),

      getActiveCall: build.query<
        {
          group: Group
          post: Post
        },
        {
          userId: number
        }
      >({
        query: ({ userId }) => ({
          url: pathApiPostsActiveCall,
          params: {
            user_id: userId,
          },
        }),
      }),

      getPostsSearch: build.query<
        Post[],
        {
          keyword: string
          fromId?: string
          only_media?: boolean
          post_owner_scope?: string
        }
      >({
        query: ({ keyword, fromId, only_media, post_owner_scope }) => ({
          url: pathApiPostsSearch,
          params: {
            keyword,
            number: LOAD_NUMBER,
            ...(fromId && fromId != '0' && { from_post_id: fromId }),
            ...(only_media && { only_media }),
            ...(post_owner_scope && { post_owner_scope }),
          },
        }),
        transformResponse: (res: { posts: Post[] }) => res.posts,
        keepUnusedDataFor: 0,
      }),

      validate: build.mutation<
        {
          result: boolean
        },
        {
          text: string
          groupId: string
          threadId?: string
        }
      >({
        query: ({ text, groupId, threadId }) => ({
          url: pathApiPostsValidate,
          method: 'POST',
          data: {
            text,
            group_id: groupId,
            ...(threadId && { thread_id: threadId }),
          },
        }),
      }),

      getGroupPostSearch: build.query<
        Post[],
        {
          groupId: string | number
          fromId?: string
          keyword: string
          onlyThreadPosts?: boolean
        }
      >({
        query: ({ groupId, keyword, fromId, onlyThreadPosts }) => ({
          url: pathApiGroupPostsSearch({ groupId }),
          params: {
            keyword,
            number: LOAD_NUMBER,
            ...(fromId && fromId != '0' && { from_post_id: fromId }),
            ...(onlyThreadPosts && { only_thread_posts: onlyThreadPosts }),
          },
        }),
        transformResponse: (res: { posts: Post[] }) => res.posts,
      }),
    }),
    overrideExisting: false,
  })

export const {
  useGetPostQuery,
  useGetPostMutateMutation,
  useGetPostLikersQuery,
  useGetPostRepostsQuery,
  useGetUserAlbumListQuery,
  useGetGroupAlbumListQuery,
  useDeleteAllPostsMutation,
  useGetUserPostsQuery,
  useDeleteSelectedPostsMutation,
  useGetMyPostsQuery,
  useGetCallTimelineQuery,
  useGetFollowersCallTimelineQuery,
  useGetGroupCallTimelineQuery,
  useGetHiddenWordsQuery,
  useCreateHiddenWordMutation,
  useUpdateHiddenWordMutation,
  useDeleteHiddenWordMutation,
  useRespondRecommendedPostMutation,
  useLikeMutation,
  useUnlikeMutation,
  useCreatePostMutation,
  useCreateRepostMutation,
  useCreateConferenceCallMutation,
  useEditPostMutation,
  useCreateSurveysVoteMutation,
  useCreateNewSharePostMutation,
  useSearchHashtagQuery,
  useGetUrlMetaDataMutation,
  useGetActiveCallQuery,
  useGetPostsSearchQuery,
  useValidateMutation,
  useGetGroupPostSearchQuery,
} = postApi
